export const META_KPI_ROW_LAYOUT = [
  ["total-spend", "impressions", "clicks", "conversions"],
  ["ctr", "cpc", "cpa", "roas"],
  ["link-clicks", "link-click-rate", "cost-per-link-click"],
  // Some backend responses report these under Meta's native metric names rather than
  // the generic ones above — same underlying data (this account's conversions are all
  // purchases, see the Conversions→Purchases rename), aliased to the same tooltip content.
  ["purchases", "cpp", "purchase-roas", "link-click-through-rate"],
  ["drop-off-impressions-clicks", "drop-off-clicks-conversions"],
] as const;

export const META_KPI_FLAT = META_KPI_ROW_LAYOUT.flat();

export const META_KPI_LOWER_IS_BETTER = new Set<(typeof META_KPI_FLAT)[number]>([
  "cpc", "cpa", "cost-per-link-click", "cpp", "drop-off-impressions-clicks", "drop-off-clicks-conversions",
]);

export type MetaKpiMetricId = (typeof META_KPI_FLAT)[number];

/** English labels (icon matcher) — same order as `META_KPI_FLAT`. */
export const META_KPI_ICON_LABELS: string[] = [
  "Total Spend",
  "Impressions",
  "Clicks",
  "Conversions",
  "CTR",
  "CPC",
  "CPP",
  "ROAS",
  "Link Clicks",
  "Link Click Rate",
  "Cost per Link Click",
  "Purchases",
  "CPP",
  "Purchase ROAS",
  "Link Click-Through Rate",
  "Drop-off: Impressions→Clicks",
  "Drop-off: Clicks→Conversions",
];

export type MetaKpiTooltipSuffix =
  | "totalSpend"
  | "impressions"
  | "clicks"
  | "conversions"
  | "ctr"
  | "cpc"
  | "cpa"
  | "roas"
  | "linkClicks"
  | "linkClickRate"
  | "costPerLinkClick"
  | "dropOffImpressionsClicks"
  | "dropOffClicksConversions";

export const META_KPI_I18N_KEY: Record<MetaKpiMetricId, MetaKpiTooltipSuffix> = {
  "total-spend": "totalSpend",
  impressions: "impressions",
  clicks: "clicks",
  conversions: "conversions",
  ctr: "ctr",
  cpc: "cpc",
  cpa: "cpa",
  roas: "roas",
  "link-clicks": "linkClicks",
  "link-click-rate": "linkClickRate",
  "cost-per-link-click": "costPerLinkClick",
  // Aliases for the Meta-native metric names — same content as their generic counterparts.
  purchases: "conversions",
  cpp: "cpa",
  "purchase-roas": "roas",
  "link-click-through-rate": "linkClickRate",
  "drop-off-impressions-clicks": "dropOffImpressionsClicks",
  "drop-off-clicks-conversions": "dropOffClicksConversions",
};

/**
 * Fallback match on the KPI's displayed title when its `id` doesn't match anything
 * above — some backend responses only expose a human title (e.g. "CPP Cost Per
 * Purchase") under an `id` we haven't guessed correctly yet, and the exact
 * punctuation/wording of that title isn't predictable either. So this matches by
 * substring rather than exact string, checked in order (most specific first);
 * values reuse the same tooltip content as their generic counterparts.
 */
const META_KPI_TITLE_ALIAS_PATTERNS: Array<{ test: RegExp; suffix: MetaKpiTooltipSuffix }> = [
  { test: /cost.*per.*link.*click/, suffix: "costPerLinkClick" },
  {
    test: /link.*click.*through.*rate|link.*ctr|link.*click.*rate|معدل النقر على الروابط|معدل النقر على الرابط|معدل النقر إلى الظهور للرابط/,
    suffix: "linkClickRate",
  },
  { test: /link.*clicks/, suffix: "linkClicks" },
  { test: /purchase.*roas|roas.*purchase/, suffix: "roas" },
  { test: /\bcpp\b|cost.*per.*purchase/, suffix: "cpa" },
  { test: /purchases/, suffix: "conversions" },
];

export const matchTitleTooltipSuffix = (title: string): MetaKpiTooltipSuffix | undefined => {
  const normalized = title.trim().toLowerCase();
  return META_KPI_TITLE_ALIAS_PATTERNS.find(({ test }) => test.test(normalized))?.suffix;
};

/** API titles use plural الروابط; display them as singular الرابط. */
export const formatMetaKpiTitle = (title: string): string =>
  title.replace(/الروابط/g, "الرابط");
